// LeadTemplatesSidebar.tsx
// Right-hand slide-in panel opened from the SEND PROPOSAL button on the lead page's
// Proposals card. Lists the active templates (search, grouped by category, expand to
// read); choosing one calls onSelect, and the card then opens the send form with it
// preselected. Same chrome as LeadTimelineSidebar.
//
// The expanded view shows the message the way THIS lead would get it: {{lead_name}},
// {{interested_in}}, {{city}}, {{agency_name}} and {{staff_name}} are replaced with real
// values. Only placeholders typed on the send form ({{amount}}, {{valid_till}}, custom
// ones) are left as written, and listed as "Filled on the next step".

import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, ChevronUp, FileText, Send, Folder, Loader2, Mail, MessageCircle, Paperclip, RotateCcw, Search, X } from 'lucide-react';
import { G, C } from '../../../components/GlobalComponents/uiTokens';
import { SHELL, INNER } from '../LeadDetails';
import { userProfileStore } from '../../../stateManagers/stores/userProfileStore';
import { PLACEHOLDER_RE, ProposalTemplate, apiError, proposalApi, varLabel } from '../../Proposals/parts/proposalTypes';

interface LeadTemplatesSidebarProps {
  visible: boolean;
  onHide: () => void;
  /** the user picked a template to send */
  onSelect: (template: ProposalTemplate) => void;
  /** the lead the proposal is for — its details fill the placeholders in the preview */
  lead?: { name?: string | null; interested_in?: string | null; city?: string | null };
}

const CHANNEL = {
  email: { label: 'Email', icon: Mail, color: C.teal },
  whatsapp: { label: 'WhatsApp', icon: MessageCircle, color: '#128c4a' },
} as const;

/** Replaces {{name}} with vals[name]; blank / unknown values leave the placeholder as written. */
const fillPlaceholders = (text: string, vals: Record<string, string>): string =>
  text.replace(PLACEHOLDER_RE, (match, name: string) => vals[name.toLowerCase()]?.trim() || match);

const LeadTemplatesSidebar: React.FC<LeadTemplatesSidebarProps> = ({ visible, onHide, onSelect, lead }) => {
  const { profile } = userProfileStore();
  const [templates, setTemplates] = useState<ProposalTemplate[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  // fresh list every time the panel opens
  useEffect(() => {
    if (!visible) return;
    setQuery('');
    setOpenId(null);
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await proposalApi.templates.list({ is_active: true });
        if (!cancelled) setTemplates(res.data ?? []);
      } catch (e) {
        if (!cancelled) setError(apiError(e, 'Could not load the templates.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [visible, reloadKey]);

  // Esc closes
  useEffect(() => {
    if (!visible) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onHide();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [visible, onHide]);

  // values the server fills itself (same names as helpers/proposal.go → prepare)
  const leadVals = useMemo<Record<string, string>>(() => {
    const staff = profile?.name?.trim() || profile?.email?.split('@')[0] || '';
    return {
      lead_name: lead?.name?.trim() ?? '',
      interested_in: lead?.interested_in?.trim() ?? '',
      city: lead?.city?.trim() ?? '',
      agency_name: profile?.agency?.name?.trim() ?? '',
      staff_name: staff,
    };
  }, [lead, profile]);

  const grouped = useMemo(() => {
    const q = query.trim().toLowerCase();
    const map = new Map<string, ProposalTemplate[]>();
    templates
      .filter((t) => !q || `${t.name} ${t.subject} ${t.category?.name ?? ''}`.toLowerCase().includes(q))
      .forEach((t) => {
        const key = t.category?.name ?? 'Other';
        map.set(key, [...(map.get(key) ?? []), t]);
      });
    return Array.from(map.entries());
  }, [templates, query]);

  if (!visible) return null;

  const panel = (
    <>
      <style>{`
        @keyframes ldv-tpl-slide-in { from { transform: translateX(100%); } to { transform: translateX(0); } }
        @keyframes ldv-tpl-spin { to { transform: rotate(360deg); } }
      `}</style>

      <div onClick={onHide} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,22,0.35)', backdropFilter: 'blur(2px)', WebkitBackdropFilter: 'blur(2px)', zIndex: 1000 }} />

      <div
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 'min(520px, 100vw)',
          background: G.shellBg,
          borderLeft: `1px solid ${G.shellBorder}`,
          boxShadow: '-8px 0 30px rgba(17,85,80,0.18)',
          zIndex: 1001,
          display: 'flex',
          flexDirection: 'column',
          animation: 'ldv-tpl-slide-in 0.22s ease-out',
        }}
      >
        {/* Header */}
        <div style={{ ...INNER, borderRadius: 0, borderTop: 'none', borderRight: 'none', borderLeft: 'none', padding: '13.8px 16.6px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '9.2px', minWidth: 0 }}>
            <div style={{ width: '35px', height: '35px', borderRadius: '10px', background: C.lime, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.teal, flexShrink: 0 }}>
              <FileText size={16} />
            </div>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '0.93rem', fontWeight: 700, color: C.teal }}>Send a proposal</div>
              <span style={{ fontSize: '0.68rem', color: C.muted }}>{loading ? 'Loading…' : `Choose a template · ${templates.length} available`}</span>
            </div>
          </div>
          <button onClick={onHide} aria-label="Close" style={{ width: '30px', height: '30px', borderRadius: '8px', border: `1px solid ${G.line}`, background: 'transparent', color: C.muted, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
            <X size={15} />
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '13.8px', display: 'flex', flexDirection: 'column', gap: '11px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '7px', padding: '0 10px', height: '34px', background: C.white, border: `1px solid ${G.line}`, borderRadius: '8px' }}>
            <Search size={13} color={C.muted} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates"
              style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', background: 'transparent', fontSize: '0.76rem', color: '#1f2937' }}
            />
          </div>

          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '7px', padding: '30px', color: C.muted, fontSize: '0.76rem' }}>
              <Loader2 size={14} style={{ animation: 'ldv-tpl-spin 0.8s linear infinite' }} /> Loading…
            </div>
          ) : error ? (
            <div style={{ padding: '14px', textAlign: 'center', fontSize: '0.76rem', color: '#c62828', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: '8.3px' }}>
              {error}
              <div style={{ marginTop: '8px' }}>
                <button type="button" onClick={() => setReloadKey((k) => k + 1)} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4px 10px', borderRadius: '6.5px', border: `1px solid ${G.line}`, background: C.white, color: C.teal, fontSize: '0.65rem', fontWeight: 800, cursor: 'pointer' }}>
                  <RotateCcw size={11} /> TRY AGAIN
                </button>
              </div>
            </div>
          ) : grouped.length === 0 ? (
            <div style={{ padding: '24px 12px', textAlign: 'center', color: C.muted, fontSize: '0.76rem', background: C.white, borderRadius: '8.3px', border: `1px dashed ${G.line}` }}>
              <FileText size={20} style={{ display: 'block', margin: '0 auto 6px', opacity: 0.35 }} />
              {query ? 'No templates match your search' : 'No active templates yet'}
            </div>
          ) : (
            grouped.map(([category, list]) => (
              <div key={category} style={SHELL}>
                <div style={{ ...INNER, padding: '12px 13px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '9px', fontSize: '0.68rem', fontWeight: 800, letterSpacing: '0.4px', textTransform: 'uppercase', color: C.sub }}>
                    <Folder size={12} /> {category}
                    <span style={{ marginLeft: 'auto', fontWeight: 700, color: C.muted }}>{list.length}</span>
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '7px' }}>
                    {list.map((t) => {
                      const meta = CHANNEL[t.channel] ?? CHANNEL.email;
                      const Icon = meta.icon;
                      const open = openId === t.id;
                      return (
                        <div key={t.id} style={{ background: C.white, border: `1px solid ${open ? C.teal : G.line}`, borderRadius: '8px', overflow: 'hidden' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 8px 6px 0' }}>
                            <button
                              type="button"
                              onClick={() => setOpenId(open ? null : t.id)}
                              style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '9px', padding: '2px 0 2px 10px', background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left' }}
                            >
                              <span style={{ width: 26, height: 26, borderRadius: '7px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: t.channel === 'email' ? C.limeDim : 'rgba(37,211,102,0.14)', color: meta.color }}>
                                <Icon size={13} />
                              </span>
                              <span style={{ flex: 1, minWidth: 0 }}>
                                <span style={{ display: 'block', fontSize: '0.76rem', fontWeight: 700, color: C.teal, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.name}</span>
                                <span style={{ display: 'block', fontSize: '0.64rem', color: C.muted }}>{meta.label}</span>
                              </span>
                              {t.attachments.length > 0 && (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '3px', fontSize: '0.64rem', fontWeight: 700, color: C.sub }}>
                                  <Paperclip size={11} /> {t.attachments.length}
                                </span>
                              )}
                              {open ? <ChevronUp size={13} color={C.muted} /> : <ChevronDown size={13} color={C.muted} />}
                            </button>
                            <button
                              type="button"
                              onClick={() => onSelect(t)}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '4.5px 11px', borderRadius: '6.5px', border: 'none', background: C.lime, color: C.teal, fontSize: '0.62rem', fontWeight: 800, letterSpacing: '0.3px', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}
                            >
                              <Send size={11} /> SEND
                            </button>
                          </div>

                          {open && (
                            <div style={{ padding: '2px 11px 11px', borderTop: `1px solid ${G.softer}` }}>
                              {t.subject && <div style={{ marginTop: '9px', fontSize: '0.74rem', fontWeight: 700, color: C.teal }}>{fillPlaceholders(t.subject, leadVals)}</div>}
                              <div style={{ marginTop: '6px', whiteSpace: 'pre-wrap', fontSize: '0.73rem', color: '#1f2937', lineHeight: 1.55, maxHeight: '240px', overflowY: 'auto' }}>{fillPlaceholders(t.body, leadVals)}</div>

                              {/* only what the lead's details can't fill — typed on the next step */}
                              {(() => {
                                const pending = t.variables.filter((v) => !leadVals[v]?.trim());
                                return pending.length > 0 ? (
                                  <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px' }}>
                                    <span style={{ fontSize: '0.62rem', color: C.muted }}>Filled on the next step:</span>
                                    {pending.map((v) => (
                                      <span key={v} style={{ padding: '1.5px 7px', borderRadius: '20px', background: G.shellBg, border: `1px solid ${G.line}`, color: C.sub, fontSize: '0.62rem', fontWeight: 700 }}>
                                        {varLabel(v)}
                                      </span>
                                    ))}
                                  </div>
                                ) : null;
                              })()}

                              {t.attachments.length > 0 && (
                                <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
                                  {t.attachments.map((a) => (
                                    <a key={a.path} href={a.url} target="_blank" rel="noopener noreferrer" style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 8px', borderRadius: '20px', background: G.shellBg, border: `1px solid ${G.line}`, color: C.teal, fontSize: '0.64rem', fontWeight: 700, textDecoration: 'none' }}>
                                      <Paperclip size={10} /> {a.file_name}
                                    </a>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </>
  );

  // portal: escapes any transformed/overflow-hidden ancestor so position:fixed is truly viewport-relative
  return createPortal(panel, document.body);
};

export default LeadTemplatesSidebar;